import { pendingChatMessageAtom } from '@/atoms/chatLaunchAtoms';
import { activeSidebarChatIdAtom } from '@/atoms/chatSidebarAtoms';
import {
  floatingAskHeidiActiveAtom,
  floatingAskHeidiPendingQueryAtom,
  floatingAskHeidiPhaseAtom,
  floatingAskHeidiSelectedChatAtom,
  floatingAskHeidiSheetVisibleAtom,
  resetFloatingAskHeidiAtom,
  userClosedSheetAtom,
  type FloatingAskHeidiPhase,
} from '@/atoms/floatingAskHeidiAtoms';
import { useIsWorkInSessionEnabled } from '@/features/flags';
import { useOpenEvidencePanel } from '@/hooks/useOpenEvidencePanel';
import { askHeidiOpened } from '@/lib/analytics/scribe/ask-ai';
import type { ChatListItem } from '@/types';
import { pendingWorkFirstTurnAtom } from '@heidi/agents/data';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

// Animation timing constants (in ms)
const ANIMATION_TIMINGS = {
  DROPDOWN_EXIT: 150, // Dropdown fade out (handled in ThreadSelector)
  INPUT_FADE_OUT: 300,
  INPUT_FADE_IN: 300, // Input fade in when returning from sidebar
  PANEL_SLIDE_IN: 400,
  CHAT_ELEMENT_STAGGER: 150,
  TOTAL_CHAT_ANIMATION: 600,
} as const;

// Phases where the sidebar panel is considered "open"
const PANEL_OPEN_PHASES = new Set<FloatingAskHeidiPhase>([
  'panel-opening',
  'chat-animating',
  'complete',
]);

interface UseFloatingAskHeidiOptions {
  onSendMessage?: (message: string) => void;
  /** Whether the Ask Heidi sidebar panel is currently open (from Redux) */
  isAskHeidiPanelOpen?: boolean;
  /** Whether transcription is currently active - triggers auto-open */
  isTranscribing?: boolean;
  /** Whether a note has been generated - triggers auto-close */
  hasNoteGenerated?: boolean;
  /**
   * Suppress the "auto-open the peek sheet when transcription starts" hybrid
   * behaviour. Used by the centre-transcription preview, where the bar floats
   * to the centre during transcription but must stay idle (placeholder-only)
   * until the user clicks into it.
   */
  suppressTranscriptionAutoOpen?: boolean;
}

interface UseFloatingAskHeidiReturn {
  phase: FloatingAskHeidiPhase;
  pendingQuery: string | null;
  /** Whether the input is active (focused or typing) */
  isActive: boolean;
  /** Whether the suggestions sheet should be visible */
  isSheetVisible: boolean;
  setPhase: (phase: FloatingAskHeidiPhase) => void;
  /** Activate the input (transition from idle to focused) */
  handleActivate: () => void;
  /** Deactivate the input (return to idle from focused/typing) */
  handleDeactivate: () => void;
  handleFocus: () => void;
  handleBlur: () => void;
  handleTextChange: (hasText: boolean) => void;
  /** Send a message and open the sidebar with animation */
  handleSendMessage: (message: string) => void;
  /** Open the sidebar directly without sending a message */
  handleOpenSidebar: () => void;
  handleOpenThread: (chat: ChatListItem) => void;
  reset: () => void;
  animationTimings: typeof ANIMATION_TIMINGS;
}

/**
 * Hook to manage the floating Ask Heidi animation state machine
 */
export function useFloatingAskHeidi(
  options: UseFloatingAskHeidiOptions = {}
): UseFloatingAskHeidiReturn {
  const {
    isAskHeidiPanelOpen = false,
    isTranscribing = false,
    hasNoteGenerated = false,
    suppressTranscriptionAutoOpen = false,
  } = options;

  const [phase, setPhase] = useAtom(floatingAskHeidiPhaseAtom);
  const pendingQuery = useAtomValue(floatingAskHeidiPendingQueryAtom);
  const setPendingQuery = useSetAtom(floatingAskHeidiPendingQueryAtom);
  const isActive = useAtomValue(floatingAskHeidiActiveAtom);
  const isSheetVisible = useAtomValue(floatingAskHeidiSheetVisibleAtom);
  const setSelectedChat = useSetAtom(floatingAskHeidiSelectedChatAtom);
  const reset = useSetAtom(resetFloatingAskHeidiAtom);

  // Track whether user has manually closed the sheet during this session
  const [userClosedSheet, setUserClosedSheet] = useAtom(userClosedSheetAtom);

  // Active sidebar chat ID - clear this to force a new thread
  const setActiveSidebarChatId = useSetAtom(activeSidebarChatIdAtom);

  // Pending chat message - set this to auto-send when sidebar opens
  const setPendingChatMessage = useSetAtom(pendingChatMessageAtom);

  // Evidence expand mode: open evidence sidebar via atom instead of Redux
  const openEvidencePanel = useOpenEvidencePanel();

  // Work-in-session: route the panel to a Work project instead of an
  // Evidence chat. `sessionId` is '' outside a session route, which both
  // gates `routeToWork` below and no-ops the (disabled-when-falsy) session
  // query, so this stays cheap/inert on every other Ask Heidi surface.
  const isWorkInSessionEnabled = useIsWorkInSessionEnabled();
  const sessionId = useCurrentSessionId();
  const { data: sessionName } = useQuerySessionById({
    sessionId,
    select: (data) => data?.session_name,
  });
  const setPendingWorkFirstTurn = useSetAtom(pendingWorkFirstTurnAtom);

  // Track timers for cleanup
  const timersRef = useRef<NodeJS.Timeout[]>([]);

  // Guard to prevent multiple returning animation triggers
  const isReturningRef = useRef(false);

  // Track previous values for edge detection
  const prevIsTranscribingRef = useRef(isTranscribing);
  const prevHasNoteGeneratedRef = useRef(hasNoteGenerated);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  // Auto-open when transcription starts (hybrid model)
  // Only triggers on the rising edge of isTranscribing (false → true)
  // Respects user's manual close choice
  useEffect(() => {
    const wasTranscribing = prevIsTranscribingRef.current;
    prevIsTranscribingRef.current = isTranscribing;

    // Only auto-open on the rising edge: transcription just started.
    // Skipped when `suppressTranscriptionAutoOpen` is set (centre-transcription
    // preview) so the centred bar stays idle until the user clicks into it.
    if (
      isTranscribing &&
      !wasTranscribing &&
      phase === 'idle' &&
      !userClosedSheet &&
      !suppressTranscriptionAutoOpen
    ) {
      setPhase('focused');
    }
  }, [isTranscribing, phase, userClosedSheet, setPhase, suppressTranscriptionAutoOpen]);

  // Auto-close when note is generated (hybrid model)
  // Only triggers on the rising edge of hasNoteGenerated (false → true)
  // Respects user's active typing - waits for them to send or close
  useEffect(() => {
    const wasNoteGenerated = prevHasNoteGeneratedRef.current;
    prevHasNoteGeneratedRef.current = hasNoteGenerated;

    // Only auto-close on the rising edge: note just generated
    // Don't close if user is typing - let them finish and send/close manually
    if (hasNoteGenerated && !wasNoteGenerated && phase === 'focused') {
      setPhase('idle');
    }
  }, [hasNoteGenerated, phase, setPhase]);

  // Watch for sidebar close and trigger returning animation
  // Also handles "stale phase on mount" - when component mounts after being hidden
  // while sidebar was open, phase may be stuck in a panel-open state
  useEffect(() => {
    const isSidebarClosed = !isAskHeidiPanelOpen;
    const isPanelPhase = PANEL_OPEN_PHASES.has(phase);

    // If sidebar is closed but phase indicates panel should be open,
    // we have a stale state that needs to be reset via returning animation.
    // This handles both:
    // 1. Normal close transition
    // 2. Component mounting with stale phase (container was unmounted while sidebar was open)
    // Guard: prevent triggering if we're already in the returning sequence
    if (isSidebarClosed && isPanelPhase && !isReturningRef.current) {
      isReturningRef.current = true;

      // Start the returning animation
      setPhase('returning');

      // After animation completes, transition to idle
      const timer = setTimeout(() => {
        setPhase('idle');
        // Clear selected chat when returning to input
        setSelectedChat(null);
        // Reset guard after sequence completes
        isReturningRef.current = false;
      }, ANIMATION_TIMINGS.INPUT_FADE_IN);

      timersRef.current.push(timer);
    }

    // Reset guard if sidebar opens again (allows next return animation)
    if (isAskHeidiPanelOpen) {
      isReturningRef.current = false;
    }
  }, [isAskHeidiPanelOpen, phase, setPhase, setSelectedChat]);

  const addTimer = useCallback((callback: () => void, delay: number) => {
    const timer = setTimeout(callback, delay);
    timersRef.current.push(timer);
    return timer;
  }, []);

  const handleActivate = useCallback(() => {
    if (phase === 'idle' || phase === 'returning') {
      setPhase('focused');
      // User is manually re-opening, so reset the user-closed flag
      // This allows auto-behaviors to work again
      setUserClosedSheet(false);
    }
  }, [phase, setPhase, setUserClosedSheet]);

  const handleDeactivate = useCallback(() => {
    if (phase === 'focused' || phase === 'typing') {
      setPhase('idle');
      // Mark that the user manually closed the sheet
      // This prevents auto-reopen during the same session
      setUserClosedSheet(true);
    }
  }, [phase, setPhase, setUserClosedSheet]);

  const handleFocus = useCallback(() => {
    if (phase === 'idle') {
      setPhase('focused');
    }
  }, [phase, setPhase]);

  const handleBlur = useCallback(() => {
    if (phase === 'focused') {
      setPhase('idle');
    }
  }, [phase, setPhase]);

  const handleTextChange = useCallback(
    (hasText: boolean) => {
      if (hasText && (phase === 'focused' || phase === 'idle')) {
        setPhase('typing');
      } else if (!hasText && phase === 'typing') {
        setPhase('focused');
      }
    },
    [phase, setPhase]
  );

  const handleSendMessage = useCallback(
    (message: string) => {
      const trimmedMessage = message.trim();
      if (!trimmedMessage) return;

      // Work-in-session only has a panel to route to inside a session —
      // everywhere else (e.g. no session route matched), fall through to
      // the existing Evidence behaviour below.
      const routeToWork = isWorkInSessionEnabled && !!sessionId;

      if (!routeToWork) {
        // Clear active sidebar chat to force a new thread (DD-011)
        // This ensures floating input queries always start fresh conversations
        setActiveSidebarChatId(null);
      }

      // Store pending query for animation display
      setPendingQuery(trimmedMessage);

      if (!routeToWork) {
        // Store pending message for ChatInput to auto-send when sidebar
        // opens.
        setPendingChatMessage(trimmedMessage);
      } else {
        // Seed before the fade timer: `openEvidencePanel()` unmounts this bar,
        // which would tear down a create mutation. `SessionWorkPanel` consumes
        // the atom. Stage immediately so a mid-animation unmount cannot drop
        // the prompt (matches `useStageAskHeidiQuery`).
        setPendingWorkFirstTurn({
          sessionId,
          prompt: trimmedMessage,
          linked: {
            type: 'session',
            sessionId,
            sessionName: sessionName?.trim() || 'Untitled session',
          },
        });
      }

      // Start the sending animation
      setPhase('sending');

      // Track analytics
      askHeidiOpened();

      // After input fades out, open the panel
      addTimer(() => {
        setPhase('panel-opening');

        openEvidencePanel();

        // Transition to complete after panel slides in
        addTimer(() => {
          setPhase('complete');
        }, ANIMATION_TIMINGS.PANEL_SLIDE_IN);
      }, ANIMATION_TIMINGS.INPUT_FADE_OUT);
    },
    [
      addTimer,
      isWorkInSessionEnabled,
      openEvidencePanel,
      sessionId,
      sessionName,
      setActiveSidebarChatId,
      setPendingChatMessage,
      setPendingQuery,
      setPendingWorkFirstTurn,
      setPhase,
    ]
  );

  const handleOpenSidebar = useCallback(() => {
    // Start the sending animation (same visual as sending, but no message)
    setPhase('sending');

    // Track analytics
    askHeidiOpened();

    // After input fades out, open the panel
    addTimer(() => {
      setPhase('panel-opening');

      openEvidencePanel();

      // Transition to complete after panel slides in
      addTimer(() => {
        setPhase('complete');
      }, ANIMATION_TIMINGS.PANEL_SLIDE_IN);
    }, ANIMATION_TIMINGS.INPUT_FADE_OUT);
  }, [addTimer, openEvidencePanel, setPhase]);

  const handleOpenThread = useCallback(
    (chat: ChatListItem) => {
      // Set the selected chat for the panel to load
      setSelectedChat(chat);

      // Start the sending animation (reuse same animation)
      setPhase('sending');

      // Track analytics
      askHeidiOpened();

      // After input fades out, open the panel
      addTimer(() => {
        setPhase('panel-opening');

        openEvidencePanel();

        // Skip chat-animating since we're loading existing thread, go straight to complete
        addTimer(() => {
          setPhase('complete');
        }, ANIMATION_TIMINGS.PANEL_SLIDE_IN);
      }, ANIMATION_TIMINGS.INPUT_FADE_OUT);
    },
    [addTimer, openEvidencePanel, setPhase, setSelectedChat]
  );

  return {
    phase,
    pendingQuery,
    isActive,
    isSheetVisible,
    setPhase,
    handleActivate,
    handleDeactivate,
    handleFocus,
    handleBlur,
    handleTextChange,
    handleSendMessage,
    handleOpenSidebar,
    handleOpenThread,
    reset,
    animationTimings: ANIMATION_TIMINGS,
  };
}
